import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { INVALID_TENANT_CODE, REPORT_NOT_FOUND } from 'common/error';
import * as ReportTypes from 'features/frontoffice/actions/types';
import { CLEAN_SESSION } from 'features/masterData/util/CommonUtil';
import { seedDefaultFilters } from 'features/globalFilters/globalFiltersActions';
import { normalizeGlobalFilterOptionLabels } from 'features/globalFilters/globalFilterOptionUtils';
import { getAllBrokers } from 'features/user/userActions';
import * as UserTypes from 'features/user/userTypes';
import { beginSwitch, clearIntent, isLatestSwitch, registerIntent } from 'features/user/tenantSwitchSequence';
import _ from 'lodash';
import { setAxiosHeaderToken } from 'networking/NetworkManger';
import * as AppActionTypes from 'store/actions/appstate';
import { store } from 'store/store';
import { formatReptSource } from 'utils/formatReptSource';
import LocalStore from 'utils/LocalStore';
import { calculateTimeWindowDates } from 'utils/timeWindowUtil';
import { postFoDetailWithRetry } from 'api/foDetailRequest';
import * as Types from './types';
import * as FavoriteTypes from '../favoriteReport/actions/types';

let foDetailRequestSeq = 0;

export const getFoDetail = (tenantCd) => {
  return (dispatch, getState) => {
    const resolvedTenantCd = tenantCd ?? getState?.()?.user?.currentTenantCd;
    const requestId = ++foDetailRequestSeq;
    return postFoDetailWithRetry('/user/fo-detail')
      .then((data) => {
        dispatch({
          type: Types.FETCH_FO_DETAIL_SUCCESS,
          payload: { ...data, tenantCd: resolvedTenantCd },
          meta: { requestId }
        });
      })
      .catch((error) => {
        // A tenant/app switch cancels in-flight requests by design.
        if (axios.isCancel(error)) {
          return;
        }
        console.error('Unable to fetch FO detail after retries:', error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Unable to load front office details right now.';
        dispatch({
          type: AppActionTypes.APPSTATE_SET_APP_ERROR,
          payload: message
        });
      });
  };
};

export const filterList = (
  searchValue,
  isPublic,
  data,
  flag,
  folders,
  folderKey
) => {
  const order = flag ? 'asc' : 'desc';
  const toDispatch = {
    searchValue: searchValue,
    isPublic: isPublic,
    data: data,
    order: order,
    folders: folders,
    folderKey: folderKey
  };
  return (dispatch) => {
    dispatch({
      type: Types.FILTER_SUCCESS,
      payload: toDispatch
    });
  };
};

export const changeOrder = (isPublic, flag, data, folders, folderKey) => {
  const toDispatch = {
    isPublic: isPublic,
    flag: flag,
    data: data,
    folders: folders,
    folderKey: folderKey
  };
  return (dispatch) => {
    dispatch({
      type: Types.SORT_SUCCESS,
      payload: toDispatch
    });
  };
};

export const getSaved = () => {
  return (dispatch) => {
    axios.get(AppConfig.FO_INITIAL_URL + '/saved-reports/').then((res) => {
      dispatch({
        type: Types.FETCH_SAVED_REPORT_SUCCESS,
        payload: res.data
      });
    });
  };
};

export const fetchMetricsByTenant = () => {
  return (dispatch) => {
    axios
      .get(AppConfig.FO_INITIAL_URL + `/metrics/findByTenant`)
      .then((res) => {
        dispatch({
          type: Types.FETCH_DEFAULT_METRIC_SUCCESS,
          payload: res.data
            .map((item) => ({
              ...item,
              order: parseInt(item.order)
            }))
            .sort((a, b) => a.order - b.order)
        });
      });
  };
};

export const saveMyMetric = (metrics) => {
  return (dispatch) => {
    axios
      .post(AppConfig.FO_INITIAL_URL + `/metrics/save`, metrics)
      .then((res) => {
        store.dispatch(fetchMetricsByUser());
      });
  };
};

const fetchMetricsByUser = () => {
  return (dispatch) => {
    axios.get(AppConfig.FO_INITIAL_URL + `/metrics`).then((res) => {
      dispatch({
        type: Types.FETCH_MY_METRIC_SUCCESS,
        payload: res.data
      });
    });
  };
};

export const dismissReport = (reportId) => {
  return (dispatch) => {
   dispatch(setSmartCardsDismissedLocal([reportId], true));
   dispatch({
      type: Types.SET_SMART_CARD_DISMISSED_LOCAL,
      payload: { reportId, dismissed: true }
    });
    dispatch({
      type: Types.SET_SMART_CARD_PENDING,
      payload: { reportId, pending: true }
    });
    return axios
      .post(AppConfig.FO_INITIAL_URL + `/init/dismiss/id/${reportId}`)
      .then((res) => {
        dispatch({
          type: Types.SET_SMART_CARD_PENDING,
          payload: { reportId, pending: false }
        });
        store.dispatch(getFoDetail());
        store.dispatch({
          type: FavoriteTypes.DISMISS_FAVORITE_REPORT,
          data: { reportId }
        });
      }).catch((error) => {
        store.dispatch({
          type: FavoriteTypes.UNDISMISS_FAVORITE_REPORT,
          data: { reportId }
        });
        dispatch({ type: Types.HIDE_SMART_CARD_FAILURE });
        dispatch({
          type: Types.SET_SMART_CARD_PENDING,
          payload: { reportId, pending: false }
        });
        dispatch({
          type: Types.SET_SMART_CARD_DISMISSED_LOCAL,
          payload: { reportId, dismissed: false }
        });
        dispatch(setSmartCardsDismissedLocal([reportId], false));
        return Promise.reject(error);
      });
  };
};

export const setSmartCardsDismissedLocal = (reportIds, dismissed) => ({
  type: Types.SET_SMART_CARDS_DISMISSED_LOCAL,
  payload: { reportIds, dismissed }
});

export const unhiddenReport = (reportIds) => {
  return (dispatch) => {
    dispatch(setSmartCardsDismissedLocal(reportIds, false));
    return axios
      .post(AppConfig.FO_INITIAL_URL + `/init/restore/id`, reportIds)
      .then((res) => {
        if(!res || res.status <200 || res.status >=300){
          throw new Error('restore api failed');
        }
        dispatch({
          type: Types.RESTORE_SMART_CARD,
          payload: reportIds
        });
        dispatch(getFoDetail());
        return res;
      })
      .catch((error) => {
         dispatch(setSmartCardsDismissedLocal(reportIds, true));
        throw error;
      });
  };
};

export const pinReport = (reportId) => {
  return (dispatch) => {
    axios
      .post(AppConfig.FO_INITIAL_URL + `/init/${reportId}/pin`)
      .then((res) => {
        store.dispatch(getFoDetail());
      });
  };
};

export const navigateToAdhoc = () => {
  return (dispatch) => {
    axios.get(AppConfig.FO_INITIAL_URL + `/tbl/url/adhoc`).then((res) => {
      window.open(res.data, '_tableau');
    });
  };
};

export const getStandardReport = () => {
  return (dispatch) => {
    dispatch({ type: Types.FETCH_REPORT_STANDARD_REQUEST });
    return axios.get(AppConfig.FO_INITIAL_URL + '/report/standard', { hideSpinner: true }).then((res) => {
      dispatch({
        type: Types.FETCH_REPORT_STANDARD_SUCCESS,
        payload: res.data
      });
    }).catch((error) => {
      dispatch({
        type: Types.FETCH_REPORT_STANDARD_FAILURE,
        payload: error
      });
    });
  };
};

export const getSmartReport = () => {
  return (dispatch) => {
    axios.get(AppConfig.FO_INITIAL_URL + '/report/smart').then((res) => {
      dispatch({
        type: Types.FETCH_REPORT_SMART_SUCCESS,
        payload: res.data
      });
    });
  };
};


export const getExplorerReport = () => {
  return (dispatch) => {
    dispatch({ type: Types.FETCH_REPORT_EXPLORER_REQUEST });
    return axios.get(AppConfig.FO_INITIAL_URL + '/report/explorer', { hideSpinner: true }).then((res) => {
      dispatch({
        type: Types.FETCH_REPORT_EXPLORER_SUCCESS,
        payload: res.data
      });
    }).catch((error) => {
      dispatch({
        type: Types.FETCH_REPORT_EXPLORER_FAILURE,
        payload: error
      });
    });
  };
};

export const getInspectionForTenant = (tenantCd) => {
  return (dispatch) => {
    if (tenantCd) {
      axios.post(AppConfig.FO_INITIAL_URL + '/init/data-through-date', tenantCd, { headers: { 'Content-Type': 'text/plain' }, hideSpinner: true }).then((res) => {
        dispatch({
          type: Types.FETCH_INSPECTION_SUCCESS,
          payload: res?.data
        });
      });
    }
  };
};

const all = 'All';

const defaultFilter = (item, filterTarget, value) => {
  return item[filterTarget]
    ? item[filterTarget].toLowerCase().includes(value.toLowerCase())
    : false;
};

export const searchFilter = (
  data,
  filterTarget,
  value,
  setData,
  filter = defaultFilter
) => {
  if (Array.isArray(data)) {
    if (value === all) {
      setData(data);
    } else {
      const newData = _.filter(data, function (o) {
        value.toLowerCase();
        return filter(o, filterTarget, value);
      });
      setData(newData);
    }
  }
};

export const fetchReportTags = () => {
  return (dispatch) => {
    axios.get(AppConfig.FO_INITIAL_URL + '/tag').then((res) => {
      dispatch({
        type: Types.FETCH_REPORT_TAGS,
        payload: res.data
      });
    });
  };
};

export const syncReportsWithDefaultingSetting = (data) => {
  return (dispatch) => {
    axios
      .post(AppConfig.FO_INITIAL_URL + '/report-default-setting/sync', data)
      .then((res) => {
        dispatch({
          type: Types.REPORT_DEFAULT_SETTING_REFRESH
        });
      });
  };
};

export const saveReportefaultingSetting = (data) => {
  return (dispatch) => {
    axios
      .post(AppConfig.FO_INITIAL_URL + '/report-default-setting', data)
      .then((res) => {
        dispatch({
          type: Types.REPORT_DEFAULT_SETTING_REFRESH
        });
      });
  };
};

export const deleteDefaultingSettingById = (id) => {
  return (dispatch) => {
    axios
      .delete(AppConfig.FO_INITIAL_URL + `/report-default-setting/${id}`)
      .then((res) => {
        dispatch({
          type: Types.REPORT_DEFAULT_SETTING_REFRESH
        });
      });
  };
};

export const fetchAllDisplayText = () => {
  return (dispatch) => {
    axios.get(AppConfig.FO_INITIAL_URL + '/report/displaytext/all').then((res) => {

      dispatch({
        type: Types.FETCH_ALL_DISPLAY_TEXT_SUCCESS,
        payload: res.data.sort()
      });
    });
  };
};

export const fetchTenantReports = (dto) => {
  return (dispatch) => {
    return axios.post(AppConfig.FO_INITIAL_URL + '/report/get/by/tenantsAndDisplayText', dto).then((res) => {

      dispatch({
        type: Types.FETCH_REPORT_TO_DELETE_SUCCESS,
        payload: res.data
      });
    });
  };
};

export const deleteTenantReports = (dto) => {
  return (dispatch) => {
    axios
      .post(
        AppConfig.FO_INITIAL_URL + `/report/delete/by/tenantsAndDisplayText`,
        dto
      )
      .catch((error) => {
        dispatch({
          type: AppActionTypes.APPSTATE_SET_APP_ERROR,
          payload: error
        });
      });
  };
};

export const addReportToTenants = (data) => {
  return (dispatch) => {
    axios
      .post(AppConfig.GEMINI_URL + '/report/add/by/name', data)
      .then((res) => {
        dispatch({
          type: AppActionTypes.APP_REST_CALL_SUCESSED,
          payload:
            'Command is fired. Thumbnail generation is running behind the scenes, it may takes hours to generate all of them!'
        });
      });
  };
};

export const globalConfig = () => {
  return ( dispatch ) => {
    store.dispatch(getAllBrokers());
    axios
      .get(
        AppConfig.FO_INITIAL_URL + `/company-admin/by/active/${true}`,
        AppConfig.POST_HEADERS
      )
      .then((res) => {
        let allCompanies = res?.data.sort((a, b) =>
          a.name?.toLowerCase() < b.name?.toLowerCase() ? -1 : 0
        );
        dispatch({
          type: Types.OPEN_GLOBAL_CONFIG_REQUEST,
          payload: {allCompanies: allCompanies }
        });
      });
  };
};

export const initTenantReports = (data) => {
  return ( dispatch ) => {
    let companyCodes = data.companies.map( ( c ) => c.code );
    axios
      .post(
        AppConfig.GEMINI_URL + `/report/sync/tenants/mode/${data.mode}`,
        companyCodes
      )
      .catch((error) => {
        throw new Error('Internal Server Error');
      });
  };
};

const getAvailableDateBoundsCacheKey = (dateBasis, tenantCd) => `${dateBasis || ''}::${tenantCd || ''}`;

export const clearAvailableDateBoundsCache = () => ({
  type: Types.CLEAR_AVAILABLE_DATE_BOUNDS_CACHE
});

const dispatchAvailableDateBounds = (dispatch, payload, timeWindow, cacheKey) => {
  const action = {
    type: Types.FETCH_AVAILABLE_DATE_BOUNDS_SUCCESS,
    payload
  };
  if (cacheKey) {
    action.meta = { cacheKey };
  }
  dispatch(action);

  if (timeWindow) {
    console.log('CALC → calling calculateTimeWindowDates(', timeWindow, ',', payload.latestDate, ',', payload.planStartDate, ')');
    const periods = calculateTimeWindowDates(timeWindow, payload.latestDate, payload.planStartDate, payload.planYearDateRanges, payload.oldestDate);
    dispatch({
      type: Types.SET_TIME_WINDOW_DATES,
      payload: periods,
      meta: { timeWindow }
    });
  }
};

export const fetchAvailableDateBoundsAndPlanYear = (dateBasis, timeWindow, tenantCd, options = {}) => {
  return (dispatch, getState = () => ({})) => {
    console.group('[GlobalFilters] fetchAvailableDateBoundsAndPlanYear');
    console.log('INPUT  → dateBasis:', dateBasis, '| timeWindow:', timeWindow, '| tenantCdOverride:', tenantCd);
    const cacheKey = getAvailableDateBoundsCacheKey(dateBasis, tenantCd);
    const cachedPayload = getState()?.fo?.availableDateBoundsCache?.[cacheKey];
    if (cachedPayload) {
      console.log('CACHE ← /available-dates response cacheKey:', cacheKey);
      dispatchAvailableDateBounds(dispatch, cachedPayload, timeWindow);
      console.groupEnd();
      return Promise.resolve(cachedPayload);
    }

    const axiosConfig = options.hideSpinner ? { hideSpinner: true } : null;
    return axios
      .post(
        AppConfig.FO_INITIAL_URL + '/global-filter/available-dates',
        { dateBasis, tenantCd },
        ...(axiosConfig ? [axiosConfig] : [])
      )
      .then((res) => {
        const latestDate = res.data.latestDate;
        const oldestDate = res.data.oldestDate;
        const planStartDate = res.data.planStartDate;
        const planYearDateRanges = res.data.planYearDateRanges;
        const planEndDate = res.data.planEndDate;
        console.log('API  ← /available-dates response (oldestDate):', oldestDate, '| latestDate:', latestDate, '| planStartDate:', planStartDate, '| planYearDateRanges:', planYearDateRanges, '| planEndDate:', planEndDate);
        dispatchAvailableDateBounds(dispatch, {
          latestDate,
          oldestDate,
          planStartDate,
          planYearDateRanges,
          planEndDate
        }, timeWindow, cacheKey);
        console.groupEnd();
      })
      .catch((err) => {
        console.error('[GlobalFilters] /available-dates error:', err.message || err);
        const errorCode = err?.response?.data?.errorCode;
        if (errorCode === 'NO_DATES_FOR_TENANT') {
          dispatch({
            type: AppActionTypes.APPSTATE_SET_APP_ERROR,
            payload: err?.response?.data?.message || 'No date information is available for this tenant.'
          });
        }
      });
  };
};

const toEpochFromUpdateTime = (updateTime) => {
  if (!Array.isArray(updateTime) || updateTime.length < 3) {
    return 0;
  }

  const [
    year,
    month,
    day,
    hour = 0,
    minute = 0,
    second = 0,
    nanos = 0,
  ] = updateTime;

  const millis = Number(nanos) / 1000000;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number.isFinite(millis) ? millis : 0
  ).getTime();
};

export const fetchGlobalFilterOptions = ({ requestKey, payload }) => {
  return async (dispatch) => {
    dispatch({
      type: Types.FETCH_GLOBAL_FILTER_OPTIONS_REQUEST,
      payload,
      meta: { requestKey },
    });

    try {
      const res = await axios.post(AppConfig.FO_INITIAL_URL + '/global-filter/options', payload, { hideSpinner: true });
      const filterOptions = res?.data?.filterOptions || {};

      const division = normalizeGlobalFilterOptionLabels(filterOptions.division);
      const plan = normalizeGlobalFilterOptionLabels(filterOptions.plan);

      dispatch({
        type: Types.FETCH_GLOBAL_FILTER_OPTIONS_SUCCESS,
        payload: { division, plan },
        meta: { requestKey },
      });
    } catch (err) {
      // A tenant/app switch cancels in-flight requests; the drawer must be able
      // to re-request rather than keep the empty option lists.
      if (axios.isCancel(err)) {
        dispatch({
          type: Types.FETCH_GLOBAL_FILTER_OPTIONS_CANCELED,
          meta: { requestKey },
        });
        return;
      }

      dispatch({
        type: Types.FETCH_GLOBAL_FILTER_OPTIONS_FAILURE,
        error: err,
        meta: { requestKey },
      });
    }
  };
};

export const resetGlobalFilterOptions = () => ({
  type: Types.RESET_GLOBAL_FILTER_OPTIONS,
});

// Tracks in-flight /cohorts/saved-reports requests keyed by tenant so concurrent
// callers (drawer preview + PopulationFilters effect) share a single request.
const inFlightCohortOptionRequests = new Map();

export const fetchCohortOptions = (tenantCd, { forceRefresh = false } = {}) => {
  return async (dispatch) => {
    if (!tenantCd) {
      dispatch({
        type: Types.FETCH_COHORT_OPTIONS_SUCCESS,
        payload: {
          cohortPublic: [],
          cohortPersonal: [],
          tenantCd: null,
        },
      });
      return;
    }

    // Coalesce concurrent requests for the same tenant. A tenant switch triggers
    // both the drawer's saved-filter preview and the PopulationFilters effect, so
    // without this the same /cohorts/saved-reports call would fire twice.
    const inFlightRequest = inFlightCohortOptionRequests.get(tenantCd);
    if (inFlightRequest && !forceRefresh) {
      return inFlightRequest;
    }

    let requestPromise;
    requestPromise = (async () => {
      try {
        // A forced refresh must start after an older request completes. Running
        // both concurrently would allow the older response to overwrite it.
        if (inFlightRequest) {
          await inFlightRequest;
        }

        const res = await axios.get(AppConfig.FO_INITIAL_URL + `/cohorts/saved-reports/${tenantCd}`, { hideSpinner: true });

        const cohortPublic = _.orderBy(
          Array.isArray(res?.data?.cohortPublic) ? res.data.cohortPublic : [],
          [(item) => toEpochFromUpdateTime(item?.updateTime)],
          ['desc']
        );

        const cohortPersonal = _.orderBy(
          Array.isArray(res?.data?.cohortPersonal) ? res.data.cohortPersonal : [],
          [(item) => toEpochFromUpdateTime(item?.updateTime)],
          ['desc']
        );

        dispatch({
          type: Types.FETCH_COHORT_OPTIONS_SUCCESS,
          payload: {
            cohortPublic,
            cohortPersonal,
            tenantCd,
          },
        });
      } catch (err) {
        // A tenant switch cancels in-flight requests by design; that is not an
        // error the user needs to see.
        if (axios.isCancel(err)) {
          return;
        }

        console.error('[GlobalFilters] Failed to fetch cohort options:', {
          tenantCd,
          message: err?.message || err,
        });

        dispatch({
          type: AppActionTypes.APPSTATE_SET_APP_ERROR,
          payload: 'Unable to load cohorts options right now.',
        });

        dispatch({
          type: Types.FETCH_COHORT_OPTIONS_SUCCESS,
          payload: {
            cohortPublic: [],
            cohortPersonal: [],
            tenantCd: null,
          },
        });
      } finally {
        if (inFlightCohortOptionRequests.get(tenantCd) === requestPromise) {
          inFlightCohortOptionRequests.delete(tenantCd);
        }
      }
    })();

    inFlightCohortOptionRequests.set(tenantCd, requestPromise);
    return requestPromise;
  };
};

export const deleteCohort = (tenantCd, luid) => {
  return async (dispatch) => {
    if (!tenantCd || !luid) {
      return { ok: false, error: 'Missing tenantCd or luid' };
    }
    try {
      await axios.delete(
        AppConfig.FO_INITIAL_URL + `/cohorts/saved-reports/${encodeURIComponent(tenantCd)}/${encodeURIComponent(luid)}`,
        { hideSpinner: true }
      );
      await dispatch(fetchCohortOptions(tenantCd, { forceRefresh: true }));
      return { ok: true };
    } catch (err) {
      console.error('[GlobalFilters] Failed to delete cohort:', {
        tenantCd,
        luid,
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message || err,
      });
      return {
        ok: false,
        status: err?.response?.status,
        error: err?.response?.data?.message || err?.message || 'Delete failed',
      };
    }
  };
};

const handleError = (dispatch, errorType, errorMessage) => {
  dispatch({ type: errorType });
  dispatch({ type: AppActionTypes.APPSTATE_SET_APP_ERROR, payload: errorMessage });
};

const getCachedReport = (tenantCd, reptSource) => {
  try {
    const history = JSON.parse(localStorage.getItem('reportHistory')) || {};
    const reports = history[tenantCd] || [];
    return reports.find(r => r && r.reptSource === reptSource) || null;
  } catch {
    return null;
  }
};

export const validateAndOpenReport = (tenantCd, workbook, dashboard) => {
  return async (dispatch, getState) => {
    const { currentApp } = getState().user;
    const { reportBaseUrl } = getState().fo;

    // This switches tenant just like userActions.fetchData, so it must raise the same
    // in-flight flag before the request. currentTenantCd holds the outgoing tenant for
    // the whole round trip, and without this the hydration guard stays satisfied and
    // tenant-scoped screens keep rendering the tenant the user is leaving.
    // Same correlation as userActions.fetchData: switches are not serialised, so only
    // the latest one may commit its tenant/JWT or lower the guard.
    const switchRequestId = beginSwitch();
    dispatch({ type: UserTypes.TENANT_SWITCH_START });
    const isSuperseded = () => !isLatestSwitch(switchRequestId);

    // Unlike fetchData, nothing here echoes committed state: the tenant comes from the
    // URL, and this opens an FO report, so the app is FO by definition -- NOT the
    // committed currentApp, which is stale mid-transition (Master Data -> FO report would
    // otherwise register and request MASTER_DATA and race the FO route initialiser).
    // Register both as the live intent so any overlapping caller coalesces onto this pair;
    // the FO initialiser's pending-intent guard then sees FO and stays quiet.
    const appCode = AppConfig.APP_CODE_FO;
    registerIntent({ tenantCd, appCode });

    axios
      .post(AppConfig.IAM_BACKEND_URL + AppConfig.APP_TENANT_SWITCHING_URL, {
        companyCode: tenantCd,
        appCode
      })
      .then((res) => {
        if (isSuperseded()) return;
        clearIntent(); // latest switch settling (every inner branch commits or fails)
        if (res) {
          const responseData = res.data;
          const { currentTenantCd, currentApp: responseApp } = responseData;
          if (window.clarity) {
            // Check for APP_SWITCH event
            if (currentApp !== responseApp) {
              window.clarity("event", "APP_SWITCH");
            }

            // Check for TENANT_SWITCH event
            if (tenantCd !== currentTenantCd) {
              window.clarity("event", "TENANT_SWITCH");
            }

            // Set Clarity custom tags
            window.clarity("set", "currentTenantCd", currentTenantCd);
            window.clarity("set", "currentApp", responseApp);
          }

          if(res?.data?.currentTenantCd === tenantCd) {
            setAxiosHeaderToken(res.data.jwt);
            LocalStore.saveAccessToken(res.data.jwt);
            dispatch({ type: UserTypes.FETCH_DATA_SUCCESS, payload: res.data });
            dispatch(seedDefaultFilters());

            axios.get(AppConfig.FO_INITIAL_URL + `/report/${tenantCd}/${workbook}/${dashboard}`)
              .then((response) => {
                if (isSuperseded()) return;
                if(response?.data?.length > 0) {
                  dispatch({
                    type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
                    reportUrl: formatReptSource(res?.data?.tbUrl , res?.data?.currentTenantCd, `${workbook}/${dashboard}`),
                    category: response.data[0].category,
                    title: response.data[0].displayText,
                    reportBaseUrl,
                    report: response.data[0]
                  });
                } else {
                  const reptSource = `${workbook}/${dashboard}`;
                  const cachedReport = getCachedReport(tenantCd, reptSource);
                  if (cachedReport) {
                    dispatch({
                      type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
                      reportUrl: formatReptSource(res?.data?.tbUrl, res?.data?.currentTenantCd, reptSource),
                      category: cachedReport.category,
                      title: cachedReport.displayText || cachedReport.name,
                      reportBaseUrl,
                      report: cachedReport
                    });
                  } else {
                    dispatch({ type: AppActionTypes.APPSTATE_SET_APP_ERROR, payload: REPORT_NOT_FOUND });
                  }
                }
              })
              .catch((err) => {
                if (isSuperseded()) return;
                // The tenant switch above already succeeded; only the report lookup
                // failed. FETCH_DATA_FAILURE would return initialState, wiping the
                // committed tenant -- and frontoffice/index.jsx does not re-bootstrap
                // on a null tenant, so the user would be stranded on the shell
                // skeleton until a refresh. Surface the error, keep the switch.
                dispatch({ type: AppActionTypes.APPSTATE_SET_APP_ERROR, payload: err?.message || err });
              });
          } else {
            handleError(dispatch, UserTypes.FETCH_DATA_FAILURE, INVALID_TENANT_CODE);
          }
        } else {
          CLEAN_SESSION();
          // CLEAN_SESSION only clears storage, so the flag above needs clearing too.
          dispatch({ type: UserTypes.FETCH_DATA_FAILURE });
        }
      })
      .catch((err) => {
        if (isSuperseded()) return;
        clearIntent();
        handleError(dispatch, UserTypes.FETCH_DATA_FAILURE, err?.message || err);
      });
  }
}

export const viewReport = (report, navigate, location) => {
  return async (dispatch, getState) => {
    const { currentTenantCd, tableauServiceUrl } = getState().user;
    const reportBaseUrl = location?.pathname;
    const [workbook, dashboard] = report.reptSource.split('/');
    navigate(`${reportBaseUrl}/${currentTenantCd}/view/${workbook}/${dashboard}`);
    dispatch({
      type: ReportTypes.OPEN_REPORT_VIEWER_REQUEST,
      reportUrl: report.reportSource ? report.reportSource : formatReptSource(tableauServiceUrl, currentTenantCd, report.reptSource),
      category: report.category,
      title: report.displayText,
      reportBaseUrl,
      report: report
    });
  }
}
